import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import {Util, UUID} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {UIRegistry} from '@api/model/ui-registry'
import {updateRef} from '@containers/element-utils'
import {globals} from '@extensions/globals-base'

interface UiElementForProps extends React.HTMLAttributes<HTMLDivElement> {
  objId: string
  children?: React.ReactNode | React.ReactNode[]
}

export const UiElementFor = forwardRef((props: UiElementForProps, ref) => {
  const {children, objId, ...htmlProps} = props
  const [element, setElement] = useState<HTMLElement | null>()
  const refCb = useCallback(
    (el: HTMLElement | null) => {
      updateRef(el, setElement)
      updateRef(el, ref)
    },
    [setElement, ref]
  )
  const handler = useRef<UUID>()

  useEffect(() => {
    if (!element) {
      return undefined
    }

    const uiReg = UIRegistry.get(globals.getCompanyId())
    const up = () => {
      if (!objId) {
        return
      }
      const ui = uiReg.get(objId)
      let cnt: HTMLElement | null = element.querySelector('.ui-cnt')
      if (!ui) {
        element.setAttribute('style', '')
        element.classList.remove('ui-hide-defaults')
        element.onclick = Util.noop
        if (cnt) {
          cnt.innerHTML = ''
        }
      } else {
        if (!cnt) {
          cnt = document.createElement('div')
          element.appendChild(cnt)
        }
        ui.render(cnt, element)
      }
    }
    const idUp = () => {
      up()
      if (objId) {
        uiReg.removeListener(handler.current)
        handler.current = uiReg.addListener(objId as DataObjectId, () => up())
      } else {
        element.classList.remove('ui-hide-defaults')
        uiReg.removeListener(handler.current)
      }
    }

    element.classList.add('ui-elem')
    const companyListener = globals.onCompanyChange(idUp)
    idUp()

    return () => {
      const uiReg = UIRegistry.get(globals.getCompanyId())
      uiReg?.removeListener(handler.current)
      globals.removeCompanyListener(companyListener)
    }
  }, [element, objId])

  return (
    <div {...htmlProps} ref={refCb}>
      {children}
    </div>
  )
})
